import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Typography,
    Grid,
    Button,
    Link,
    makeStyles,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import InventoryIcon from "@mui/icons-material/Inventory";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { useMutation } from "@apollo/client";

import { BeaconWrapUpStory } from "../../../api/graphQL/queryModels";
import { useUserContext } from "../auth/UserContextProvider";
import WrapUpStoryArchive from "./WrapUpStoryArchive";
import ArchivedDisplay from "../items/ArchivedDisplay";
import { useAppContext } from "../../../AppContextProvider";
import { APPROVE_BEACON_WRAP_UP_STORY } from "../../../api/graphQL/mutations";
import { useNotifications } from "../notifications/NotificationContextProvider";

interface ComponentProps {
    story: BeaconWrapUpStory;
}

const getStatusLabel = (isApproved: boolean, isPublished: boolean): string => {
    if (isApproved) return "APPROVED";
    if (isPublished) return "REVIEWED";
    return "UNREVIEWED";
};

const getStatusClassName = (
    isApproved: boolean,
    isPublished: boolean,
    classes: { approvedMarker: string; reviewedMarker: string; unReviewedMarker: string }
): string => {
    if (isApproved) return classes.approvedMarker;
    if (isPublished) return classes.reviewedMarker;
    return classes.unReviewedMarker;
};

const canUserEditWrapUpStory = (userId: string | undefined, story: BeaconWrapUpStory, isContentAdmin: boolean): boolean => {
    if (!userId) return false;
    if (userId === story.author?.id || isContentAdmin) return true;
    return (story.project?.participants || []).some((participant) => !!participant && participant.person.id === userId);
};

interface ProjectInfoProps {
    story: BeaconWrapUpStory;
    projectsAppUri: string | undefined;
    className: string;
}

const ProjectInfo = ({ story, projectsAppUri, className }: ProjectInfoProps) => {
    if (!story.project) return null;
    return (
        <>
            <Typography variant="h6" color="inherit" className={className}>
                {story.project.clients?.map((client) => (
                    <Link color="inherit" href={`${projectsAppUri}/clients/${client.id}`} key={client.id}>
                        {client.name}
                    </Link>
                ))}
                {story.project.clients ? " \u2022 " : ""}
                <Link color="inherit" href={`${projectsAppUri}/projects/${story.project.id}`}>
                    {story.project.name}
                </Link>
            </Typography>
            <Typography variant="h6" color="inherit" className={className}>
                {story.project.studios.map((s) => s.name).join(" \u2022 ") || ""}
            </Typography>
        </>
    );
};

interface ApproveDialogProps {
    isOpen: boolean;
    isApproved: boolean;
    isApproving: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

const ApproveDialog = ({ isOpen, isApproved, isApproving, onClose, onConfirm }: ApproveDialogProps) => (
    <Dialog open={isOpen} aria-labelledby="approve-dialog-title">
        <DialogTitle id="approve-dialog-title">{isApproved ? "Unapprove" : "Approve"}</DialogTitle>
        <DialogContent>
            <DialogContentText id="approve-dialog-description">
                {isApproved
                    ? "Are you sure you want to unapprove this wrap-up story?"
                    : "Are you sure you want to approve this wrap-up story for external sharing?"}
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} variant="outlined" disabled={isApproving}>
                No, go back
            </Button>
            <Button onClick={onConfirm} variant="contained" color="primary" disabled={isApproving}>
                {isApproved ? "Yes, unapprove" : "Yes, approve"}
            </Button>
        </DialogActions>
    </Dialog>
);

const useStyles = makeStyles((theme) => ({
    storyHeader: {
        color: "#fff"
    },
    background: {
        backgroundSize: "cover",
        backgroundPosition: "center",
        background: "linear-gradient(90deg, #000, #00675B)"
    },
    overlay: {
        paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(5),
        paddingBottom: theme.spacing(8),
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(2)
        }
    },
    contentProject: {
        opacity: 0.75
    },
    editButton: {
        color: theme.palette.common.white
    },
    button: {
        color: theme.palette.common.white,
        borderColor: theme.palette.common.white,
        marginBottom: theme.spacing(2.3),
        marginLeft: theme.spacing(1.5)
    },
    wrapUpBadge: {
        backgroundColor: "#00675B",
        color: theme.palette.common.white,
        fontWeight: 700,
        marginBottom: theme.spacing(1)
    },
    unReviewedMarker: {
        background: theme.palette.warning.main,
        borderBottomLeftRadius: 8,
        color: theme.palette.getContrastText(theme.palette.warning.main),
        marginLeft: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    reviewedMarker: {
        background: theme.palette.primary.main,
        borderBottomLeftRadius: 8,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        marginLeft: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    approvedMarker: {
        background: theme.palette.success.main,
        borderBottomLeftRadius: 8,
        color: theme.palette.common.white,
        marginLeft: theme.spacing(1),
        marginBottom: theme.spacing(1)
    }
}));

const WrapUpStoryHeader = (props: ComponentProps) => {
    const classes = useStyles();
    const { story } = props;

    const { user, isContentAdmin } = useUserContext();
    const { context } = useAppContext();
    const { setNotification } = useNotifications();
    const [isOpen, setIsOpen] = React.useState(false);
    const [isApproveDialogOpen, setIsApproveDialogOpen] = React.useState(false);

    const canEdit = canUserEditWrapUpStory(user?.id, story, isContentAdmin);

    const canApprove = isContentAdmin && story.isPublished && !story.isArchived;

    const [approveBeaconWrapUpStory, { loading: isApproving }] = useMutation(APPROVE_BEACON_WRAP_UP_STORY);

    const handleApproveConfirm = async () => {
        const willApprove = !story.isApproved;
        try {
            await approveBeaconWrapUpStory({
                variables: { id: story.id, approve: willApprove }
            });
            const message = willApprove ? "Wrap-up story approved" : "Wrap-up story unapproved";
            setNotification(message, "success");
        } catch {
            setNotification("Failed to update approval status", "error");
        }
        setIsApproveDialogOpen(false);
    };

    const archiveLabel = story.isArchived ? "Unarchive" : "Archive";
    const approveLabel = story.isApproved ? "Unapprove" : "Approve";

    return (
        <div className={classes.storyHeader}>
            <div className={classes.background}>
                <div className={classes.overlay}>
                    <Grid container justifyContent="space-between">
                        <Grid item>
                            <Chip label="WRAP-UP" className={classes.wrapUpBadge} size="small" />
                            {!story.isArchived && (
                                <Chip
                                    label={getStatusLabel(story.isApproved, story.isPublished)}
                                    className={getStatusClassName(story.isApproved, story.isPublished, classes)}
                                    size="small"
                                />
                            )}
                            <Typography variant="h3" color="inherit">
                                {story.title}
                            </Typography>
                        </Grid>
                        <Grid item>
                            {story.isArchived && <ArchivedDisplay />}
                            {!story.isArchived && canEdit ? (
                                <Button
                                    component={RouterLink}
                                    to={`/wrap-up-stories/${story.id}/edit`}
                                    startIcon={<EditIcon className={classes.editButton} />}
                                    className={classes.button}
                                    variant="outlined"
                                >
                                    {"Edit"}
                                </Button>
                            ) : null}
                            {canEdit ? (
                                <Button
                                    onClick={() => setIsOpen(true)}
                                    startIcon={<InventoryIcon className={classes.editButton} />}
                                    className={classes.button}
                                    variant="outlined"
                                >
                                    {archiveLabel}
                                </Button>
                            ) : null}
                            {canApprove ? (
                                <Button
                                    onClick={() => setIsApproveDialogOpen(true)}
                                    startIcon={<CheckCircleIcon className={classes.editButton} />}
                                    className={classes.button}
                                    variant="outlined"
                                    disabled={isApproving}
                                >
                                    {approveLabel}
                                </Button>
                            ) : null}
                            {isOpen && <WrapUpStoryArchive isOpen={isOpen} setIsOpen={setIsOpen} />}
                            <ApproveDialog
                                isOpen={isApproveDialogOpen}
                                isApproved={story.isApproved}
                                isApproving={isApproving}
                                onClose={() => setIsApproveDialogOpen(false)}
                                onConfirm={handleApproveConfirm}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <ProjectInfo
                                story={story}
                                projectsAppUri={context?.settings?.relatedAppsUri?.projectsApp}
                                className={classes.contentProject}
                            />
                        </Grid>
                    </Grid>
                </div>
            </div>
        </div>
    );
};

export default WrapUpStoryHeader;
